import React, { useState } from 'react';
import { Plus, Trash2, Repeat, Play, Settings } from 'lucide-react';
import { Resource } from '../../../types';

interface PatternMakerConfigFormProps {
  resource: Resource;
  config?: Record<string, any>;
  onConfigChange: (config: Partial<Resource>) => void;
}

interface PatternElement {
  type: string;
  color: string;
}

export const PatternMakerConfigForm: React.FC<PatternMakerConfigFormProps> = ({
  resource,
  config,
  onConfigChange
}) => {
  const mergedConfig = { ...resource, ...config };
  const [elements, setElements] = useState<PatternElement[]>(mergedConfig.elements || []);
  const [maxLength, setMaxLength] = useState<number>(mergedConfig.maxLength || 8);
  const [showPattern, setShowPattern] = useState<boolean>(mergedConfig.showPattern !== undefined ? mergedConfig.showPattern : true);
  const [allowInteraction, setAllowInteraction] = useState<boolean>(mergedConfig.allowInteraction !== undefined ? mergedConfig.allowInteraction : true);
  const [question, setQuestion] = useState<string>(mergedConfig.question || '');
  const [autoPlay, setAutoPlay] = useState<boolean>(mergedConfig.autoPlay !== undefined ? mergedConfig.autoPlay : false);
  const [playSpeed, setPlaySpeed] = useState<number>(mergedConfig.playSpeed || 1000);

  const availableShapes = ['circle', 'square', 'triangle', 'star', 'diamond', 'hexagon'];
  const availableColors = ['red', 'blue', 'green', 'yellow', 'purple', 'orange', 'pink', 'brown'];
  const maxLengthOptions = [4, 6, 8, 10, 12, 16];
  const playSpeedOptions = [500, 1000, 1500, 2000, 3000];

  // Remove the problematic useEffect that was causing infinite loops
  // Instead, we'll call onConfigChange only when explicitly needed

  const addElement = () => {
    const newElement: PatternElement = {
      type: 'circle',
      color: 'red'
    };
    const newElements = [...elements, newElement];
    setElements(newElements);
    onConfigChange({
      ...resource,
      elements: newElements
    });
  };

  const updateElement = (index: number, field: keyof PatternElement, value: any) => {
    const updatedElements = [...elements];
    updatedElements[index] = { ...updatedElements[index], [field]: value };
    setElements(updatedElements);
    onConfigChange({
      ...resource,
      elements: updatedElements
    });
  };

  const removeElement = (index: number) => {
    const newElements = elements.filter((_, i) => i !== index);
    setElements(newElements);
    onConfigChange({
      ...resource,
      elements: newElements
    });
  };

  const moveElement = (index: number, direction: 'up' | 'down') => {
    if (direction === 'up' && index > 0) {
      const newElements = [...elements];
      [newElements[index], newElements[index - 1]] = [newElements[index - 1], newElements[index]];
      setElements(newElements);
      onConfigChange({
        ...resource,
        elements: newElements
      });
    } else if (direction === 'down' && index < elements.length - 1) {
      const newElements = [...elements];
      [newElements[index], newElements[index + 1]] = [newElements[index + 1], newElements[index]];
      setElements(newElements);
      onConfigChange({
        ...resource,
        elements: newElements
      });
    }
  };

  const getColorValue = (colorName: string) => {
    const colorMap: Record<string, string> = {
      'red': '#EF4444',
      'blue': '#3B82F6',
      'green': '#10B981',
      'yellow': '#F59E0B',
      'purple': '#8B5CF6',
      'orange': '#F97316',
      'pink': '#EC4899',
      'brown': '#A0522D'
    };
    return colorMap[colorName] || colorName;
  };

  const getShapeIcon = (shape: string) => {
    const iconMap: Record<string, string> = {
      'circle': '●',
      'square': '■',
      'triangle': '▲',
      'star': '⭐',
      'diamond': '◆',
      'hexagon': '⬡'
    };
    return iconMap[shape] || '●';
  };

  const generateSamplePattern = () => {
    if (elements.length === 0) return [];
    
    const pattern: PatternElement[] = [];
    const repeatCount = Math.ceil(maxLength / elements.length);
    
    for (let i = 0; i < repeatCount; i++) {
      pattern.push(...elements);
    }
    
    return pattern.slice(0, maxLength);
  };

  const samplePattern = generateSamplePattern();

  return (
    <div className="space-y-6">
      {/* Question Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou consigne
        </label>
        <textarea
          value={question}
          onChange={(e) => {
            const newQuestion = e.target.value;
            setQuestion(newQuestion);
            onConfigChange({
              ...resource,
              question: newQuestion
            });
          }}
          placeholder="Ex: Créez un rythme et continuez-le"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
      </div>

      {/* Pattern Elements Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Repeat className="w-5 h-5 mr-2" />
            Éléments du pattern
          </h3>
          <button
            type="button"
            onClick={addElement}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un élément</span>
          </button>
        </div>

        <div className="space-y-3">
          {elements.map((element, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Élément {index + 1}
                </span>
                <div className="flex items-center space-x-2">
                  <button
                    type="button"
                    onClick={() => moveElement(index, 'up')}
                    disabled={index === 0}
                    className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Déplacer vers le haut"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    onClick={() => moveElement(index, 'down')}
                    disabled={index === elements.length - 1}
                    className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Déplacer vers le bas"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    onClick={() => removeElement(index)}
                    className="text-red-600 hover:text-red-800"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Shape */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Forme
                  </label>
                  <select
                    value={element.type}
                    onChange={(e) => updateElement(index, 'type', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableShapes.map(shape => (
                      <option key={shape} value={shape}>
                        {shape}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <select
                    value={element.color}
                    onChange={(e) => updateElement(index, 'color', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {availableColors.map(color => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Aperçu:</span>
                <div
                  className="w-6 h-6 border border-gray-300 rounded flex items-center justify-center text-sm"
                  style={{ backgroundColor: getColorValue(element.color) }}
                >
                  {getShapeIcon(element.type)}
                </div>
              </div>
            </div>
          ))}
        </div>

        {elements.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <Repeat className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>Aucun élément configuré</p>
            <p className="text-sm">Ajoutez des éléments pour créer votre pattern</p>
          </div>
        )}
      </div>

      {/* Pattern Settings */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
          <Settings className="w-5 h-5 mr-2" />
          Paramètres du pattern
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Longueur maximale
            </label>
            <select
              value={maxLength}
              onChange={(e) => {
                const newMaxLength = parseInt(e.target.value);
                setMaxLength(newMaxLength);
                onConfigChange({
                  ...resource,
                  maxLength: newMaxLength
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {maxLengthOptions.map(length => (
                <option key={length} value={length}>
                  {length} éléments
                </option>
              ))}
            </select>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Afficher le pattern
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={showPattern}
                onChange={(e) => {
                  const newShowPattern = e.target.checked;
                  setShowPattern(newShowPattern);
                  onConfigChange({
                    ...resource,
                    showPattern: newShowPattern
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {showPattern ? 'Oui' : 'Non'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Playback Settings */}
      <div>
        <h3 className="text-lg font-medium text-gray-900 flex items-center mb-4">
          <Play className="w-5 h-5 mr-2" />
          Paramètres de lecture
        </h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Lecture automatique
            </label>
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={autoPlay}
                onChange={(e) => {
                  const newAutoPlay = e.target.checked;
                  setAutoPlay(newAutoPlay);
                  onConfigChange({
                    ...resource,
                    autoPlay: newAutoPlay
                  });
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-600">
                {autoPlay ? 'Oui' : 'Non'}
              </span>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vitesse de lecture
            </label>
            <select
              value={playSpeed}
              onChange={(e) => {
                const newPlaySpeed = parseInt(e.target.value);
                setPlaySpeed(newPlaySpeed);
                onConfigChange({
                  ...resource,
                  playSpeed: newPlaySpeed
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {playSpeedOptions.map(speed => (
                <option key={speed} value={speed}>
                  {speed}ms ({speed / 1000}s)
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Pattern Preview */}
      {elements.length > 0 && (
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-4">Aperçu du pattern</h3>
          
          <div className="p-4 border border-gray-200 rounded-lg bg-white">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm font-medium text-gray-700">
                Pattern complet ({samplePattern.length} éléments)
              </span>
              <span className="text-xs text-gray-500">
                Répété {Math.ceil(maxLength / elements.length)} fois
              </span>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {samplePattern.map((element, index) => (
                <div
                  key={index}
                  className="w-8 h-8 border border-gray-300 rounded flex items-center justify-center text-sm"
                  style={{ backgroundColor: getColorValue(element.color) }}
                  title={`Élément ${index + 1}: ${element.type} ${element.color}`}
                >
                  {getShapeIcon(element.type)}
                </div>
              ))}
            </div>
            
            <div className="mt-3 text-xs text-gray-500">
              <p>• Les éléments se répètent automatiquement pour atteindre la longueur maximale</p>
              <p>• Les élèves devront continuer ce pattern en ajoutant les bons éléments</p>
            </div>
          </div>
        </div>
      )}

      {/* Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Paramètres généraux</h3>
        
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Permettre l'interaction
            </label>
            <p className="text-xs text-gray-500">
              Permettre aux élèves de créer et continuer des patterns
            </p>
          </div>
          <input
            type="checkbox"
            checked={allowInteraction}
            onChange={(e) => {
              const newAllowInteraction = e.target.checked;
              setAllowInteraction(newAllowInteraction);
              onConfigChange({
                ...resource,
                allowInteraction: newAllowInteraction
              });
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Résumé de la configuration</h4>
        <div className="text-xs text-blue-800 space-y-1">
          <p>• {elements.length} élément(s) de pattern configuré(s)</p>
          <p>• Longueur maximale: {maxLength} éléments</p>
          <p>• Pattern affiché: {showPattern ? 'Oui' : 'Non'}</p>
          <p>• Lecture automatique: {autoPlay ? 'Oui' : 'Non'}</p>
          {autoPlay && <p>• Vitesse: {playSpeed}ms par élément</p>}
          {question && <p>• Question: "{question}"</p>}
        </div>
      </div>
    </div>
  );
};
